import { Card } from "@redotech/arbiter/card";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import type { RedoListItem } from "@redotech/arbiter/list/redo-list";
import { RedoCurrencySelector } from "@redotech/arbiter/select-dropdown/redo-currency-selector";
import { RedoSingleSelectDropdownInput } from "@redotech/arbiter/select-dropdown/redo-single-select-dropdown-input";
import { DropoffPricingMode } from "@redotech/congo-enums/dropoff-pricing-mode";
import { Currency } from "@redotech/money/currencies";
import {
  defaultDropoffFeeUsd,
  DropoffProviderType,
  getMerchantFacingProviderName,
  HAPPY_RETURNS_PROVIDER,
} from "@redotech/redo-return-dropoff-provider-common/dropoff-provider";
import { groupInput, input, InputSchema, listInput } from "@redotech/ui/form";
import { memo } from "react";

const MODE_OPTIONS: RedoListItem<DropoffPricingMode>[] = [
  {
    id: DropoffPricingMode.Default,
    type: "text",
    text: "Standard price",
    value: DropoffPricingMode.Default,
  },
  {
    id: DropoffPricingMode.Fixed,
    type: "text",
    text: "Fixed price",
    value: DropoffPricingMode.Fixed,
  },
  {
    id: DropoffPricingMode.Markup,
    type: "text",
    text: "Markup over standard price",
    value: DropoffPricingMode.Markup,
  },
];

/** A markup is a flat amount or a percentage, never both. */
export const MarkupType = { Flat: "flat", Percent: "percent" } as const;
export type MarkupType = (typeof MarkupType)[keyof typeof MarkupType];

const MARKUP_TYPE_OPTIONS: RedoListItem<MarkupType>[] = [
  {
    id: MarkupType.Flat,
    type: "text",
    text: "Flat amount",
    supportingText: "Add a fixed amount to the standard price.",
    value: MarkupType.Flat,
  },
  {
    id: MarkupType.Percent,
    type: "text",
    text: "Percentage",
    supportingText: "A percentage of the network's standard price",
    value: MarkupType.Percent,
  },
];

function numberErrors(
  value: string,
  { positive, max }: { positive: boolean; max?: number },
) {
  const num = Number(value);
  if (value.trim() === "" || isNaN(num)) {
    return ["Must be a valid number"];
  }
  if (positive ? num <= 0 : num < 0) {
    return [positive ? "Must be greater than 0" : "Must be 0 or more"];
  }
  if (max !== undefined && num > max) {
    return [`Must be ${max} or less`];
  }
  return [];
}

interface PricingErrors {
  amount: string[];
  markupAmount: string[];
  markupPercent: string[];
}

/**
 * Only the visible field is validated, so a hidden one never blocks a save.
 * The chosen markup field must be positive, matching the server's rule that a
 * markup carries exactly one of the two.
 */
function pricingErrors(value: {
  mode: DropoffPricingMode;
  markupType: MarkupType;
  amount: string;
  markupAmount: string;
  markupPercent: string;
}): PricingErrors {
  const markup = value.mode === DropoffPricingMode.Markup;
  return {
    amount:
      value.mode === DropoffPricingMode.Fixed
        ? numberErrors(value.amount, { positive: true })
        : [],
    markupAmount:
      markup && value.markupType === MarkupType.Flat
        ? numberErrors(value.markupAmount, { positive: true })
        : [],
    markupPercent:
      markup && value.markupType === MarkupType.Percent
        ? numberErrors(value.markupPercent, { positive: true, max: 100 })
        : [],
  };
}

/** The currency the standard price is quoted in, where it is known up front. */
function standardPriceCurrency(
  provider: DropoffProviderType,
): Currency | undefined {
  return provider === HAPPY_RETURNS_PROVIDER ? undefined : Currency.USD;
}

function standardPriceText(provider: DropoffProviderType): string {
  if (provider === HAPPY_RETURNS_PROVIDER) {
    return "the negotiated Happy Returns fee";
  }
  return `USD ${defaultDropoffFeeUsd[provider] ?? 0}`;
}

export const boxlessDropoffsForm = listInput(
  () =>
    groupInput(
      {
        provider: input<DropoffProviderType>(),
        mode: input<DropoffPricingMode>(),
        markupType: input<MarkupType>(),
        amount: input<string>(),
        currency: input<Currency>(),
        markupAmount: input<string>(),
        markupPercent: input<string>(),
      },
      {
        validator: (value) => {
          const errors = pricingErrors(value);
          return [
            ...errors.amount,
            ...errors.markupAmount,
            ...errors.markupPercent,
          ];
        },
      },
    ),
  (provider: DropoffProviderType) => ({
    provider,
    mode: DropoffPricingMode.Default,
    markupType: MarkupType.Flat,
    amount: "0",
    currency: Currency.USD,
    markupAmount: "0",
    markupPercent: "0",
  }),
  (value) => value.provider,
);

export type BoxlessDropoffsForm = InputSchema.Input<typeof boxlessDropoffsForm>;
export type BoxlessDropoffsValue = InputSchema.Value<
  typeof boxlessDropoffsForm
>;

export const BoxlessDropoffsSettings = memo(function BoxlessDropoffsSettings({
  input,
}: {
  input: BoxlessDropoffsForm;
}) {
  return (
    <Flex justify="flex-start" w="full">
      <Flex flexDirection="column" gap="3xl" w="half">
        {input.inputs.map((feeInput) => {
          const { provider, mode, markupType } = feeInput.value;
          const providerName = getMerchantFacingProviderName(provider);
          const { amount, currency, markupAmount, markupPercent } =
            feeInput.inputs;
          const errors = pricingErrors(feeInput.value);
          return (
            <Card key={provider} title={providerName}>
              <Flex flexDirection="column" gap="lg">
                <RedoSingleSelectDropdownInput<DropoffPricingMode>
                  description={`The standard price is ${standardPriceText(provider)}`}
                  label="Pricing"
                  options={MODE_OPTIONS}
                  optionSelected={({ item }) =>
                    feeInput.inputs.mode.setValue(item.value)
                  }
                  selectedItem={MODE_OPTIONS.find(
                    (option) => option.value === mode,
                  )}
                  size="xs"
                />
                {mode === DropoffPricingMode.Fixed && (
                  <Flex>
                    <RedoTextInput
                      description={`The amount Redo charges the merchant per ${providerName} drop-off`}
                      errorText={errors.amount[0]}
                      inputTypeHint="number"
                      label="Drop-off fee"
                      min={0}
                      setValue={amount.setValue}
                      step={0.01}
                      value={amount.value}
                    />
                    <RedoCurrencySelector
                      label="Currency"
                      setValue={(value) =>
                        currency.setValue(
                          Object.values(Currency).find((c) => c === value) ??
                            Currency.USD,
                        )
                      }
                      value={currency.value}
                    />
                  </Flex>
                )}
                {mode === DropoffPricingMode.Markup && (
                  <>
                    <RedoSingleSelectDropdownInput<MarkupType>
                      label="Markup type"
                      options={MARKUP_TYPE_OPTIONS}
                      optionSelected={({ item }) => {
                        feeInput.inputs.markupType.setValue(item.value);
                        // Clear the other field, so a markup never carries both.
                        if (item.value === MarkupType.Flat) {
                          markupPercent.setValue("0");
                        } else {
                          markupAmount.setValue("0");
                        }
                      }}
                      selectedItem={MARKUP_TYPE_OPTIONS.find(
                        (option) => option.value === markupType,
                      )}
                      size="xs"
                    />
                    <Flex>
                      {markupType === MarkupType.Flat ? (
                        <RedoTextInput
                          description="Added to the standard price, in its currency"
                          errorText={errors.markupAmount[0]}
                          inputTypeHint="number"
                          label="Flat markup"
                          min={0}
                          prefix={standardPriceCurrency(provider)}
                          setValue={markupAmount.setValue}
                          step={0.01}
                          value={markupAmount.value}
                        />
                      ) : (
                        <RedoTextInput
                          description="A percentage of the network's standard price"
                          errorText={errors.markupPercent[0]}
                          inputTypeHint="number"
                          label="Percent markup"
                          max={100}
                          min={0}
                          setValue={markupPercent.setValue}
                          step={0.01}
                          suffix="%"
                          value={markupPercent.value}
                        />
                      )}
                    </Flex>
                  </>
                )}
              </Flex>
            </Card>
          );
        })}
      </Flex>
    </Flex>
  );
});
